<template>
    <v-container fluid class="customer-page pa-4 pa-md-6">
        <!-- Header -->
        <div class="d-flex align-center mb-6">
            <div>
                <h1 class="text-h5 font-weight-bold grey--text text--darken-3">
                    <v-icon size="28" color="primary" class="mr-2">mdi-account-group</v-icon>
                    Quản Lý Khách Hàng
                </h1>
                <p class="text-body-2 grey--text mt-1 mb-0">
                    Tổng cộng {{ totalCustomers }} khách hàng
                </p>
            </div>
            <v-spacer></v-spacer>
            <v-btn color="primary" depressed rounded @click="showAddDialog" class="text-none px-5">
                <v-icon left small>mdi-plus</v-icon>
                Thêm Khách Hàng
            </v-btn>
        </div>

        <!-- Search & Filters -->
        <v-card class="mb-4 rounded-lg" flat style="border: 1px solid #e0e0e0">
            <v-card-text class="pa-4">
                <v-row align="center">
                    <v-col cols="12" md="5">
                        <v-text-field v-model="search" placeholder="Tìm theo tên, mã, số điện thoại..." clearable
                            single-line hide-details outlined dense rounded
                            prepend-inner-icon="mdi-magnify"></v-text-field>
                    </v-col>
                    <v-spacer></v-spacer>
                    <v-col cols="auto" class="d-none d-md-flex">
                        <v-btn-toggle v-model="viewMode" mandatory dense borderless>
                            <v-btn small value="table">
                                <v-icon small>mdi-format-list-bulleted</v-icon>
                            </v-btn>
                            <v-btn small value="card">
                                <v-icon small>mdi-view-grid</v-icon>
                            </v-btn>
                        </v-btn-toggle>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <!-- Table View -->
        <v-card v-if="viewMode === 'table'" class="rounded-lg" flat style="border: 1px solid #e0e0e0">
            <v-data-table :headers="headers" :items="tableData" :page.sync="page" :items-per-page="itemsPerPage"
                hide-default-footer :loading="loading" class="customer-table" loading-text="Đang tải dữ liệu ..."
                @click:row="viewDetail" fixed-header>
                <template v-slot:[`item.name`]="{ item }">
                    <div class="d-flex align-center py-2" style="min-width: 200px">
                        <v-avatar :color="getAvatarColor(item.name)" size="38" class="mr-3 flex-shrink-0">
                            <span class="white--text text-body-2 font-weight-medium">{{
                                item.name.charAt(0).toUpperCase()
                            }}</span>
                        </v-avatar>
                        <div style="min-width: 0">
                            <div class="text-body-2 font-weight-medium text-truncate" style="max-width: 550px">
                                {{ item.name }}
                            </div>
                            <div class="text-caption grey--text">{{ item.code }}</div>
                        </div>
                    </div>
                </template>
                <template v-slot:[`item.phone_number`]="{ item }">
                    <span class="text-body-2" v-if="item.phone_number">
                        <v-icon x-small color="grey" class="mr-1">mdi-phone</v-icon>
                        {{ item.phone_number }}
                    </span>
                    <span v-else class="grey--text text--lighten-1">—</span>
                </template>
                <template v-slot:[`item.address`]="{ item }">
                    <div v-if="item.address" class="text-body-2 text-truncate" style="max-width: 220px"
                        :title="item.address">
                        {{ item.address }}
                    </div>
                    <span v-else class="grey--text text--lighten-1">—</span>
                </template>
                <template v-slot:[`item.total_debt`]="{ item }">
                    <span class="text-body-2 font-weight-medium">{{ formatCurrency(item.total_debt) }}</span>
                </template>
                <template v-slot:[`item.paid_amount`]="{ item }">
                    <span class="text-body-2 success--text">{{ formatCurrency(item.paid_amount) }}</span>
                </template>
                <template v-slot:[`item.remaining_debt`]="{ item }">
                    <v-chip :color="getRemainingColor(item)" small label class="font-weight-medium">
                        {{ formatCurrency(Number(item.total_debt) - Number(item.paid_amount)) }}
                    </v-chip>
                </template>
                <template v-slot:[`item.action`]="{ item }">
                    <v-menu offset-y left>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon small v-bind="attrs" v-on="on" @click.stop>
                                <v-icon small>mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item @click="viewDetail(item)">
                                <v-list-item-icon class="mr-2">
                                    <v-icon small color="primary">mdi-eye</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title class="text-body-2">Xem chi tiết</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="confirmDelete(item)">
                                <v-list-item-icon class="mr-2">
                                    <v-icon small color="error">mdi-delete</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title class="text-body-2 error--text">Xóa</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>
            </v-data-table>
            <v-divider></v-divider>
            <div class="d-flex align-center justify-space-between pa-3">
                <span class="text-caption grey--text">
                    Hiển thị {{ (page - 1) * itemsPerPage + 1 }}–{{ Math.min(page * itemsPerPage, totalCustomers) }}
                    trên {{ totalCustomers }}
                </span>
                <v-pagination v-model="page" :length="pageCount" @input="changePage" :total-visible="7" circle
                    class="my-0"></v-pagination>
            </div>
        </v-card>

        <!-- Card View -->
        <div v-else>
            <v-row>
                <v-col v-for="item in tableData" :key="item.id" cols="12" sm="6" md="4" lg="3">
                    <v-card class="rounded-lg customer-card" flat style="border: 1px solid #e0e0e0"
                        @click="viewDetail(item)">
                        <v-card-text class="pa-4">
                            <div class="d-flex align-center mb-3">
                                <v-avatar :color="getAvatarColor(item.name)" size="44" class="mr-3">
                                    <span class="white--text text-h6">{{
                                        item.name.charAt(0).toUpperCase()
                                    }}</span>
                                </v-avatar>
                                <div style="min-width: 0; flex: 1">
                                    <div class="text-subtitle-2 font-weight-bold text-truncate">{{ item.name }}</div>
                                    <div class="text-caption grey--text">{{ item.code }}</div>
                                </div>
                                <v-menu offset-y left>
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop>
                                            <v-icon small>mdi-dots-vertical</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list dense>
                                        <v-list-item @click="viewDetail(item)">
                                            <v-list-item-title class="text-body-2">
                                                <v-icon small class="mr-1">mdi-eye</v-icon> Xem chi tiết
                                            </v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="confirmDelete(item)">
                                            <v-list-item-title class="text-body-2 error--text">
                                                <v-icon small color="error" class="mr-1">mdi-delete</v-icon> Xóa
                                            </v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                            </div>
                            <v-divider class="mb-3"></v-divider>
                            <div v-if="item.phone_number" class="d-flex align-center mb-2">
                                <v-icon x-small color="grey" class="mr-2">mdi-phone</v-icon>
                                <span class="text-caption">{{ item.phone_number }}</span>
                            </div>
                            <div v-if="item.address" class="d-flex align-start mb-3">
                                <v-icon x-small color="grey" class="mr-2 mt-1">mdi-map-marker</v-icon>
                                <span class="text-caption text-truncate-2">{{ item.address }}</span>
                            </div>
                            <div class="rounded pa-3" style="background: #f5f5f5">
                                <div class="d-flex justify-space-between mb-1">
                                    <span class="text-caption grey--text">Tổng nợ</span>
                                    <span class="text-caption font-weight-medium">{{ formatCurrency(item.total_debt)
                                        }}</span>
                                </div>
                                <div class="d-flex justify-space-between mb-1">
                                    <span class="text-caption grey--text">Đã thanh toán</span>
                                    <span class="text-caption success--text">{{ formatCurrency(item.paid_amount)
                                        }}</span>
                                </div>
                                <v-divider class="my-1"></v-divider>
                                <div class="d-flex justify-space-between">
                                    <span class="text-caption font-weight-medium">Còn lại</span>
                                    <v-chip :color="getRemainingColor(item)" x-small label class="font-weight-bold">
                                        {{ formatCurrency(Number(item.total_debt) - Number(item.paid_amount)) }}
                                    </v-chip>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
            <div class="d-flex justify-center mt-4">
                <v-pagination v-model="page" :length="pageCount" @input="changePage" :total-visible="7"
                    circle></v-pagination>
            </div>
        </div>

        <!-- Loading overlay for card view -->
        <div v-if="viewMode === 'card' && loading" class="d-flex justify-center pa-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <!-- Add Customer Dialog -->
        <v-dialog v-model="addDialog" max-width="600px" persistent>
            <v-card class="rounded-lg">
                <v-card-title class="pa-5 pb-3">
                    <v-icon color="primary" class="mr-2">mdi-account-plus</v-icon>
                    <span class="text-h6">Thêm Khách Hàng</span>
                    <v-spacer></v-spacer>
                    <v-btn icon small @click="closeAddDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pa-5">
                    <v-form ref="addForm">
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="newCustomer.name" label="Tên khách hàng *"
                                    :rules="[rules.required]" outlined dense></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="newCustomer.code" label="Mã khách hàng *"
                                    :rules="[rules.required]" outlined dense></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="newCustomer.phone_number" label="Số điện thoại" outlined
                                    dense></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field v-model="newCustomer.address" label="Địa chỉ" outlined
                                    dense></v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-textarea v-model="newCustomer.description" label="Mô tả" outlined dense
                                    rows="3"></v-textarea>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeAddDialog" class="text-none">Hủy</v-btn>
                    <v-btn color="primary" depressed @click="saveCustomer" :loading="saving" class="text-none px-6">
                        <v-icon left small>mdi-check</v-icon>
                        Lưu
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { debounce } from "lodash";
import { getCustomers, addCustomer, deleteCustomer } from "@/api/customer";

const AVATAR_COLORS = [
    "#1976D2", "#388E3C", "#F57C00", "#7B1FA2",
    "#C62828", "#00838F", "#4527A0", "#AD1457",
    "#2E7D32", "#0277BD", "#EF6C00", "#6A1B9A",
];

export default {
    data() {
        return {
            page: 1,
            pageCount: 1,
            itemsPerPage: 10,
            totalCustomers: 0,
            tableData: [],
            loading: false,
            search: "",
            viewMode: "table",
            addDialog: false,
            saving: false,
            newCustomer: {
                name: "",
                code: "",
                email: "",
                phone_number: "",
                address: "",
                description: "",
                note: "",
                total_debt: 0,
                paid_amount: 0,
            },
            rules: {
                required: (value) => !!value || "Trường này là bắt buộc",
                email: (value) => {
                    if (!value) return true;
                    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    return pattern.test(value) || "Email không hợp lệ";
                },
            },
            headers: [
                {
                    text: "Khách hàng",
                    value: "name",
                    sortable: true,
                    width: "60%",
                },
                {
                    text: "Số điện thoại",
                    value: "phone_number",
                    sortable: true,
                    width: "12%",
                },
                // {
                //     text: "Địa chỉ",
                //     value: "address",
                //     sortable: true,
                //     width: "18%",
                // },
                {
                    text: "Tổng nợ",
                    align: "end",
                    sortable: true,
                    value: "total_debt",
                    width: "13%",
                },
                {
                    text: "Đã thanh toán",
                    align: "end",
                    sortable: true,
                    value: "paid_amount",
                    width: "13%",
                },
                {
                    text: "Còn lại",
                    align: "end",
                    sortable: true,
                    value: "remaining_debt",
                    width: "13%",
                },
                {
                    text: "",
                    value: "action",
                    sortable: false,
                    align: "center",
                    width: "50px",
                },
            ],
        };
    },
    created() {
        this.getData();
    },
    watch: {
        search: debounce(async function (val) {
            this.loading = true;
            this.page = 1;
            let data = await getCustomers({
                perPage: this.itemsPerPage,
                search: val,
            });
            this.loading = false;
            this.tableData = data.data;
            this.pageCount = data.meta.last_page;
            this.totalCustomers = data.meta.total || data.data.length;
        }, 300),
    },
    methods: {
        async getData() {
            this.loading = true;
            try {
                let data = await getCustomers({
                    page: this.page,
                    perPage: this.itemsPerPage,
                    search: this.search,
                });
                this.tableData = data.data;
                this.pageCount = data.meta.last_page;
                this.totalCustomers = data.meta.total || data.data.length;
            } catch (error) {
                this.$toast.error("Không thể tải dữ liệu");
            } finally {
                this.loading = false;
            }
        },
        changePage(val) {
            this.page = val;
            this.getData();
        },
        viewDetail(item) {
            this.$router.push("/khachhang/" + item.id);
        },
        showAddDialog() {
            this.addDialog = true;
            this.newCustomer = {
                name: "",
                code: "",
                email: "",
                phone_number: "",
                address: "",
                description: "",
                note: "",
                total_debt: 0,
                paid_amount: 0,
            };
            if (this.$refs.addForm) {
                this.$refs.addForm.resetValidation();
            }
        },
        closeAddDialog() {
            this.addDialog = false;
        },
        async saveCustomer() {
            if (!this.$refs.addForm.validate()) {
                return;
            }
            this.saving = true;
            try {
                const response = await addCustomer(this.newCustomer);
                this.$toast.success("Thêm khách hàng thành công");
                this.closeAddDialog();
                const newId = response?.data?.id;
                if (newId) {
                    this.$router.push("/khachhang/" + newId);
                } else {
                    this.getData();
                }
            } catch (error) {
                this.$toast.error(
                    error.response?.data?.message || "Không thể thêm khách hàng"
                );
            } finally {
                this.saving = false;
            }
        },
        confirmDelete(item) {
            this.$confirmBox.show({
                title: "Xác nhận xóa",
                width: 480,
                body:
                    "Bạn có chắc chắn muốn xóa khách hàng <strong>" +
                    item.name +
                    "</strong>?",
                action: () => deleteCustomer(item.id),
                onDone: () => {
                    this.$toast.success("Xóa khách hàng thành công");
                    this.getData();
                },
            });
        },
        formatCurrency(value) {
            if (!value) return "0 ₫";
            return new Intl.NumberFormat("vi-VN", {
                style: "currency",
                currency: "VND",
            }).format(value);
        },
        getAvatarColor(name) {
            if (!name) return AVATAR_COLORS[0];
            let hash = 0;
            for (let i = 0; i < name.length; i++) {
                hash = name.charCodeAt(i) + ((hash << 5) - hash);
            }
            return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
        },
        getRemainingColor(item) {
            const remaining = Number(item.total_debt) - Number(item.paid_amount);
            if (remaining <= 0) return "success";
            if (remaining < Number(item.total_debt) * 0.3) return "warning";
            return "error";
        },
    },
};
</script>

<style scoped>
.customer-page {
    max-width: 1400px;
    margin: 0 auto;
}

.customer-table {
    border-radius: 8px;
}

.customer-table >>> tbody tr {
    cursor: pointer;
    transition: background-color 0.15s;
}

.customer-table >>> tbody tr:hover {
    background-color: #f5f8ff !important;
}

.customer-table >>> thead th {
    font-size: 0.75rem !important;
    font-weight: 600 !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #757575 !important;
    white-space: nowrap;
}

.customer-card {
    cursor: pointer;
    transition: all 0.2s;
}

.customer-card:hover {
    border-color: #1976D2 !important;
    box-shadow: 0 2px 12px rgba(25, 118, 210, 0.15) !important;
    transform: translateY(-1px);
}

.text-truncate-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

@media only screen and (max-width: 600px) {
    .customer-page {
        padding: 8px !important;
    }
}
</style>
